// Daten der Mieter-Detailseite (Handoff Mieter §5.3). Erst der Mieter, dann alle Bereiche
// parallel; ein fehlgeschlagener Bereich blendet nur sich selbst aus (fehler[bereich]).
// Saldo und Monate kommen nicht von hier, sondern aus dem gemeinsamen Mietkonto (useMietkonto).

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { heuteIso } from '../immobilien/format';
import { ladeAktivitaeten, ladeNotizen } from '../kontakte/api';
import type { Aktivitaet, KontaktNotiz } from '../kontakte/types';
import type { ObjektKopfRoh } from '../immobilien/objektKopf';
import type { TicketZeile, VertragZeile, ZaehlerZeile } from '../immobilien/useObjektDetail';
import { zaehlerDerEinheit } from '../immobilien/einheitDetail';
import type { MieterListenRoh } from './liste';
import { MIETER_DOK_SPALTEN, type MieterDokument } from './mieterDokumente';

export type MieterRoh = MieterListenRoh & Record<string, unknown>;

export type DokumentKurz = MieterDokument;
export interface TerminKurz { id: string; titel: string | null; start_datum: string; typ: string | null }
export type ZaehlerKurz = ZaehlerZeile & { mieter_id: string | null };

export interface MieterDetailDaten {
  mieter: MieterRoh;
  objekt: ObjektKopfRoh | null;
  einheit: { id: string; bezeichnung: string | null } | null;
  /** profiles.status des Portal-Zugangs (Rolle mieter, über E-Mail) */
  portalStatus: string | null;
  mitmieter: number;
  vertraege: VertragZeile[];
  dokumente: DokumentKurz[];
  /** anstehende Termine (ab heute) */
  termine: TerminKurz[];
  tickets: TicketZeile[];
  zaehler: ZaehlerKurz[];
  notizen: KontaktNotiz[];
  aktivitaeten: Aktivitaet[];
}

export type MieterBereich = Exclude<keyof MieterDetailDaten, 'mieter'>;

export type MieterDetailZustand =
  | { status: 'laedt' }
  | { status: 'fehlt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; daten: MieterDetailDaten; fehler: Partial<Record<MieterBereich, string>> };

const LEER: Omit<MieterDetailDaten, 'mieter'> = {
  objekt: null, einheit: null, portalStatus: null, mitmieter: 0,
  vertraege: [], dokumente: [], termine: [], tickets: [], zaehler: [], notizen: [], aktivitaeten: [],
};

function wert<T>(r: { data: T | null; error: { message: string } | null }): T {
  if (r.error) throw new Error(r.error.message);
  return r.data as T;
}

async function ladeBereiche(m: MieterRoh): Promise<{ daten: Omit<MieterDetailDaten, 'mieter'>; fehler: Partial<Record<MieterBereich, string>> }> {
  const heute = heuteIso();
  const email = (m.email ?? '').trim();
  const lader: { [K in MieterBereich]: () => Promise<MieterDetailDaten[K]> } = {
    objekt: async () => (m.objekt_id ? wert(await supabase.from('objekte').select('*').eq('id', m.objekt_id).maybeSingle()) as ObjektKopfRoh | null : null),
    einheit: async () => (m.einheit_id ? wert(await supabase.from('objekt_einheiten').select('id, bezeichnung').eq('id', m.einheit_id).maybeSingle()) : null),
    portalStatus: async () => {
      if (!email) return null;
      const rows = wert(await supabase.from('profiles').select('status').eq('role', 'mieter').ilike('email', email.replace(/[\\%_]/g, '\\$&'))) as { status: string | null }[];
      return rows.some(r => r.status === 'Aktiv') ? 'Aktiv' : rows[0]?.status ?? null;
    },
    mitmieter: async () => {
      const r = await supabase.from('entity_links').select('id', { count: 'exact', head: true })
        .eq('link_type', 'mitmieter').eq('source_type', 'mieter').eq('source_id', m.id);
      if (r.error) throw new Error(r.error.message);
      return r.count ?? 0;
    },
    vertraege: async () => wert(await supabase.from('vertraege')
      .select('id, vertragsnummer, vertragsart, mieter_id, einheit, beginn, ende, unbefristet, kaltmiete, status, mietanpassung_typ, kuendigungsfrist_monate')
      .eq('mieter_id', m.id).order('beginn', { ascending: false })) as VertragZeile[],
    dokumente: async () => wert(await supabase.from('dokumente').select(MIETER_DOK_SPALTEN)
      .eq('mieter_id', m.id).eq('is_deleted', false).order('created_at', { ascending: false }).limit(200)) as DokumentKurz[],
    termine: async () => wert(await supabase.from('termine').select('id, titel, start_datum, typ')
      .eq('mieter_id', m.id).gte('start_datum', heute).order('start_datum', { ascending: true }).limit(50)) as TerminKurz[],
    tickets: async () => wert(await supabase.from('tickets')
      .select('id, ticket_nummer, titel, status, prioritaet, created_at, faellig_am, einheit_id, mieter_id, eskaliert')
      .eq('mieter_id', m.id).is('geloescht_am', null).order('created_at', { ascending: false }).limit(200)) as TicketZeile[],
    zaehler: async () => {
      if (!m.objekt_id) return [];
      const alle = wert(await supabase.from('zaehler').select('*')
        .eq('objekt_id', m.objekt_id).order('typ')) as ZaehlerKurz[];
      // Zähler der Einheit über die Bezeichnung (wie auf der Einheit-Seite) oder direkt am Mieter
      let bez = (m.einheit ?? '').trim();
      if (m.einheit_id) {
        const e = await supabase.from('objekt_einheiten').select('bezeichnung').eq('id', m.einheit_id).maybeSingle();
        bez = ((e.data as { bezeichnung: string | null } | null)?.bezeichnung ?? '').trim() || bez;
      }
      return zaehlerDerEinheit(alle, bez, [m.id]);
    },
    notizen: async () => ladeNotizen('mieter', m.id),
    aktivitaeten: async () => ladeAktivitaeten('mieter', m.id),
  };
  const schluessel = Object.keys(lader) as MieterBereich[];
  const ergebnisse = await Promise.allSettled(schluessel.map(k => lader[k]()));
  const daten: Record<string, unknown> = { ...LEER };
  const fehler: Partial<Record<MieterBereich, string>> = {};
  ergebnisse.forEach((r, i) => {
    const k = schluessel[i]!;
    if (r.status === 'fulfilled') daten[k] = r.value;
    else fehler[k] = r.reason instanceof Error ? r.reason.message : 'Fehler';
  });
  return { daten: daten as Omit<MieterDetailDaten, 'mieter'>, fehler };
}

/** Gemeldet von Panels nach Änderungen, damit Kopf, Widgets und Seitenleiste neu laden. */
export const MIETER_GEAENDERT = 'immo-mieter-geaendert';
export function meldeMieterGeaendert(mieterId: string): void {
  window.dispatchEvent(new CustomEvent(MIETER_GEAENDERT, { detail: mieterId }));
}

export function useMieterDetail(mieterId: string): { zustand: MieterDetailZustand; neuLaden: () => void } {
  const [zustand, setZustand] = useState<MieterDetailZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      const r = await supabase.from('mieter').select('*').eq('id', mieterId).maybeSingle();
      if (!aktiv) return;
      if (r.error) { setZustand({ status: 'fehler', meldung: r.error.message }); return; }
      if (!r.data) { setZustand({ status: 'fehlt' }); return; }
      const mieter = r.data as MieterRoh;
      const { daten, fehler } = await ladeBereiche(mieter);
      if (aktiv) setZustand({ status: 'ok', daten: { mieter, ...daten }, fehler });
    })().catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [mieterId, runde]);

  useEffect(() => {
    const hoeren = (e: Event) => { if ((e as CustomEvent<string>).detail === mieterId) setRunde(x => x + 1); };
    window.addEventListener(MIETER_GEAENDERT, hoeren);
    return () => window.removeEventListener(MIETER_GEAENDERT, hoeren);
  }, [mieterId]);

  const neuLaden = useCallback(() => setRunde(x => x + 1), []);
  return { zustand, neuLaden };
}
